import React, { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, UsersIcon } from '@heroicons/react/24/outline';
import CollaboratorIA from '@/features/Collaborators/IA/CollaboratorIA';
import { Collaborator } from '@/features/Collaborators/Models/collaborator';
import { useAuth, ROLE_ADMIN, ROLE_COLLABORATOR_ADMIN, ROLE_COLLABORATOR_VIEWER, ROLE_COLLABORATOR_EDITOR } from '@/contexts/AuthContext';
import { checkPendingEvaluation } from '@/features/People/Evaluations/services/evaluationsService';
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll';
import { COLLABORATOR_NAVIGATION } from './collaboratorNavigation.definitions';

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}

interface CollaboratorNavigationProps {
    collaborators: Collaborator[];
    isLoading: boolean;
}

const CollaboratorNavigation: React.FC<CollaboratorNavigationProps> = ({ collaborators, isLoading }) => {
    const location = useLocation();
    const { user, userHasRole } = useAuth();
    const [pendingCampaignId, setPendingCampaignId] = React.useState<string | null>(null);
    const {
        scrollRef,
        canScrollPrev,
        canScrollNext,
        hasOverflow,
        scrollPrev,
        scrollNext,
        sync,
    } = useHorizontalScroll<HTMLElement>();

    React.useEffect(() => {
        const check = async () => {
            if (user?.collaboratorId) {
                const CACHE_KEY = `radar_notification_cache_${user.collaboratorId}`;
                const CACHE_TTL_MS = 60 * 1000;

                try {
                    const cachedRaw = localStorage.getItem(CACHE_KEY);
                    if (cachedRaw) {
                        const { id, timestamp } = JSON.parse(cachedRaw);
                        const age = Date.now() - timestamp;

                        if (age < CACHE_TTL_MS) {
                            setPendingCampaignId(id);
                            return;
                        }
                    }

                    const id = await checkPendingEvaluation(user.collaboratorId);
                    setPendingCampaignId(id);

                    localStorage.setItem(CACHE_KEY, JSON.stringify({
                        id,
                        timestamp: Date.now()
                    }));
                } catch (e) {
                    console.error("Error checking pending evaluation", e);
                }
            }
        };
        check();
    }, [user?.collaboratorId]);

    const filteredNavigation = useMemo(() => {
        if (!user) return [];

        return COLLABORATOR_NAVIGATION.filter(item => {
            if (!item.requiredRoles || item.requiredRoles.length === 0) {
                return true;
            }
            return item.requiredRoles.some(role => userHasRole(role));
        });
    }, [user, userHasRole]);

    React.useEffect(() => {
        sync();
    }, [filteredNavigation.length, sync]);

    const canSeeIA = userHasRole(ROLE_ADMIN) || userHasRole(ROLE_COLLABORATOR_ADMIN) || userHasRole(ROLE_COLLABORATOR_VIEWER) || userHasRole(ROLE_COLLABORATOR_EDITOR);

    return (
        <header className="bg-white shadow-lg ring-1 ring-slate-100 rounded-3xl mb-8 p-2 flex flex-col gap-2 overflow-hidden sm:flex-row sm:items-center sm:justify-between">
            <div className="flex shrink-0 items-center px-6 py-3 bg-gradient-to-r from-slate-50 to-white rounded-2xl sm:mr-4">
                <div className="bg-indigo-600 rounded-lg p-2 mr-3 shadow-md shadow-indigo-200">
                    <UsersIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                    <h1 className="text-lg font-bold text-slate-800 leading-none whitespace-nowrap">Colaboradores</h1>
                    <span className="text-xs text-slate-500 font-medium whitespace-nowrap">Gestão e Desenvolvimento</span>
                </div>
            </div>
            <div className="relative flex w-full min-w-0 items-center sm:flex-grow">
                {canScrollPrev && (
                    <div
                        className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center bg-gradient-to-r from-white via-white to-transparent pr-10">
                        <button
                            type="button"
                            onClick={scrollPrev}
                            aria-label="Ver abas anteriores"
                            className="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-500 shadow-md ring-1 ring-slate-200 transition-colors hover:text-indigo-600 hover:ring-indigo-200"
                        >
                            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>
                )}

                {canScrollNext && (
                    <div
                        className="pointer-events-none absolute inset-y-0 right-0 z-10 flex items-center justify-end bg-gradient-to-l from-white via-white to-transparent pl-10">
                        <button
                            type="button"
                            onClick={scrollNext}
                            aria-label="Ver próximas abas"
                            className="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-500 shadow-md ring-1 ring-slate-200 transition-colors hover:text-indigo-600 hover:ring-indigo-200"
                        >
                            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>
                )}

                <nav
                    ref={scrollRef}
                    aria-label="Seções de Colaboradores"
                    className={classNames(
                        'flex w-full snap-x snap-proximity items-center gap-1 overflow-x-auto scroll-smooth p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                        hasOverflow ? 'pr-2' : ''
                    )}
                >
                    {filteredNavigation.map((item) => (
                        <NavLink
                            key={item.name}
                            to={item.href}
                            className={({ isActive }) =>
                                classNames(
                                    'group flex flex-shrink-0 snap-start items-center gap-2 px-4 py-2.5 rounded-2xl transition-all duration-200 relative whitespace-nowrap',
                                    isActive
                                        ? 'bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-200 font-semibold'
                                        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700 font-medium'
                                )
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <item.icon
                                        className={`h-5 w-5 transition-colors ${isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-500'}`}
                                        aria-hidden="true"
                                    />
                                    <span className="text-sm">{item.name}</span>

                                    {item.name === 'Avaliações' && pendingCampaignId && (
                                        <span className="absolute top-1 right-1 flex h-2.5 w-2.5">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500 ring-2 ring-white"></span>
                                        </span>
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>
            </div>
        </header>
    );
};

export default CollaboratorNavigation;
